import React, { FC, useMemo, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormBaseType, FormConfigDto, UserRole } from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";

import CreateFormDialog from "../CreateUpdateFormDialog";
import NoFormsYet from "./components/NoFormsYet";

import { Chip, ConfirmationModal, PageTitle } from "@components/common";
import { Button, Card, CardContent, CircularProgress } from "@components/base";
import { useDeleteFormConfig, useGetFormsConfigs } from "@api";
import Table, { TableColumn } from "@components/common/Table";
import { useUserStore } from "@store/user";

interface FormBuilderList {}

const FormBuilderList: FC<FormBuilderList> = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [createFormDialogOpen, setCreateFormDialogOpen] = useState<boolean>(false);
  const [deleteForm, setDeleteForm] = useState<FormConfigDto>();
  const { user } = useUserStore();
  const { formsConfigs, formsConfigEnabled, isFetchingFormsConfigs } =
    useGetFormsConfigs(user);
  const { deleteFormConfig, deleteFormConfigEnabled } = useDeleteFormConfig(user);

  const title = t("formBuilder.title");

  const handleEdit = (form: FormConfigDto): void => {
    navigate(`/form-builder/${form.id}`);
  };

  const handleDelete = (form: FormConfigDto): void => {
    setDeleteForm(form);
  };

  const handleCreateForm = (): void => {
    setCreateFormDialogOpen(true);
  };

  const forms = useMemo(
    () =>
      formsConfigs?.items.filter((form) =>
        user?.role === UserRole.Admin
          ? true
          : form.isCustomTemplate || form.formBaseType === FormBaseType.Custom,
      ) ?? [],
    [formsConfigs?.items],
  );

  const columns: TableColumn<FormConfigDto>[] = [
    {
      title: t("common.forms"),
      key: "name",
      renderCellContent: (form) => form.name,
    },
    {
      title: t("formBuilder.formBaseType"),
      key: "type",
      renderCellContent: (form) => (
        <Stack direction="row" spacing={1}>
          <Chip
            label={FormBaseType[form.formBaseType]}
            type="outlined"
            color="orange1"
            size="medium"
          />
          {!form.isCustomTemplate && form.formBaseType !== FormBaseType.Custom && (
            <Chip
              label={t("formBuilder.genericTemplate")}
              type="outlined"
              color="green"
              size="medium"
            />
          )}
        </Stack>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      renderCellContent: (form) => (
        <>
          <IconButton
            onClick={() => handleEdit(form)}
            color="primary"
            disabled={
              user?.role === UserRole.Admin
                ? false
                : form.formBaseType !== FormBaseType.Custom && !form.isCustomTemplate
            }
          >
            <FeatherIcon icon="edit-3" size={18} />
          </IconButton>
          {deleteFormConfigEnabled && (
            <IconButton
              onClick={() => handleDelete(form)}
              color="error"
              disabled={
                form.formBaseType !== FormBaseType.Custom && !form.isCustomTemplate
              }
            >
              <FeatherIcon icon="trash-2" size={18} />
            </IconButton>
          )}
        </>
      ),
      tableCellProps: {
        align: "center",
        width: 120,
      },
    },
  ];

  return (
    formsConfigEnabled && (
      <Box>
        <Card>
          <CardContent>
            <Stack
              width="100%"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Box>
                <PageTitle title={title} />
                <Typography variant="subtitle1">{title}</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FeatherIcon icon="plus" />}
                onClick={handleCreateForm}
              >
                {t("formBuilder.create")}
              </Button>
            </Stack>
            {isFetchingFormsConfigs ? (
              <CircularProgress />
            ) : formsConfigs ? (
              <Table<FormConfigDto>
                data={forms}
                columns={columns}
                dataPlaceholder={
                  <Box p={6}>
                    <Typography variant="subtitle2" mb={3}>
                      {t("formBuilder.noCustomFormTemplates")}
                    </Typography>
                    <Typography variant="body1" mb={3}>
                      {t("formBuilder.noCustomFormTemplatesDescription")}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<FeatherIcon icon="plus" />}
                      onClick={handleCreateForm}
                      size="small"
                    >
                      {t("formBuilder.create")}
                    </Button>
                  </Box>
                }
                tableProps={{
                  size: "small",
                }}
              />
            ) : (
              <NoFormsYet onCreateForm={handleCreateForm} />
            )}
          </CardContent>
        </Card>
        <CreateFormDialog
          open={createFormDialogOpen}
          onClose={() => setCreateFormDialogOpen(false)}
        />
        <ConfirmationModal
          open={!!deleteForm}
          title={t("formBuilder.deleteForm")}
          description={t("formBuilder.deleteFormConfirmation", {
            formName: deleteForm?.name,
          })}
          onClose={() => setDeleteForm(undefined)}
          onConfirm={() => {
            if (deleteForm) {
              deleteFormConfig(deleteForm.id);
              setDeleteForm(undefined);
            }
          }}
        />
      </Box>
    )
  );
};

export default FormBuilderList;
